import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import {
  Alert, Button, Empty, Select, Space, Typography, Spin, Tag, Table, message
} from 'antd';
import { PlusOutlined, ReloadOutlined, EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ColumnsType } from 'antd/es/table';
import {
  supabase,
  getCurrentOrgId,
  mapJobToClientRequestStatus,
  getClientRequestStatusColor,
  isClientRequestOverdueUnscheduled,
  type Job,
  type JobLeg,
  type ClientRequestStatus,
} from '@ubus/shared';
import { translateClientRequestStatus } from '../utils/clientStatusLabels';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

type JobRow = Job & { job_legs: JobLeg[] };

function shortPlace(address?: string | null): string {
  if (!address?.trim()) return '—';
  return address.split(',')[0]?.trim() || address.trim();
}

function requestRouteLabel(job: JobRow, t: TFunction): string {
  const leg = job.job_legs?.[0];
  if (leg?.pickup_address && leg?.dropoff_address) {
    return `${shortPlace(leg.pickup_address)} → ${shortPlace(leg.dropoff_address)}`;
  }
  const title = job.title ?? '';
  if (/smoke[-_]?clt|smoke[-_]?test|accept-path|reject-path/i.test(title)) {
    return t('common.tripRequest');
  }
  const afterDot = title.split('·')[1]?.trim();
  if (afterDot) return afterDot;
  if (title.startsWith('Request ')) {
    return title.replace(/^Request\s+\d{4}-\d{2}-\d{2}\s*/, '').trim() || t('common.tripRequest');
  }
  return title || t('common.tripRequest');
}

function Requests() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<JobRow[]>([]);
  const [hasTrips, setHasTrips] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ClientRequestStatus | 'all'>('all');

  useEffect(() => {
    let cancelled = false;
    getCurrentOrgId()
      .then((id) => {
        if (!cancelled) setOrgId(id);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setOrgId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchRows = useCallback(async () => {
    if (orgId === undefined) return;
    setLoading(true);
    try {
      if (!orgId) {
        setRows([]);
        setHasTrips(false);
        return;
      }

      const [jobsRes, tripsRes] = await Promise.all([
        supabase
          .from('jobs')
          .select('*, job_legs(*)')
          .eq('org_id', orgId)
          .eq('source', 'client_request')
          .order('schedule_date', { ascending: false })
          .order('created_at', { ascending: false }),
        supabase
          .from('trips')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', orgId)
          .limit(1),
      ]);

      if (jobsRes.error) throw jobsRes.error;
      const jobs = (jobsRes.data ?? []) as JobRow[];
      for (const job of jobs) {
        job.job_legs = (job.job_legs ?? []).sort((a, b) => a.sequence - b.sequence);
      }
      setRows(jobs);
      setHasTrips((tripsRes.count ?? 0) > 0);
    } catch (err) {
      console.error(err);
      message.error(t('requests.loadFailed'));
      setRows([]);
      setHasTrips(false);
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(
    () =>
      rows.filter((job) => {
        if (statusFilter === 'all') return true;
        return mapJobToClientRequestStatus(job) === statusFilter;
      }),
    [rows, statusFilter]
  );

  const openTrip = async (job: JobRow, e?: MouseEvent) => {
    e?.stopPropagation();
    const tripId = job.job_legs?.find((l) => l.trip_id)?.trip_id;
    if (tripId) {
      navigate(`/trip/${tripId}`);
      return;
    }
    const { data } = await supabase
      .from('trips')
      .select('id')
      .eq('job_id', job.id)
      .limit(1)
      .maybeSingle();
    if (data?.id) navigate(`/trip/${data.id}`);
  };

  const openDetail = (job: JobRow) => {
    navigate(`/requests/${job.id}`);
  };

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all' as const, label: t('common.allStatuses') },
      { value: 'submitted' as const, label: translateClientRequestStatus(t, 'submitted') },
      { value: 'accepted' as const, label: translateClientRequestStatus(t, 'accepted') },
      { value: 'scheduled' as const, label: translateClientRequestStatus(t, 'scheduled') },
      { value: 'rejected' as const, label: translateClientRequestStatus(t, 'rejected') },
    ],
    [t]
  );

  const columns: ColumnsType<JobRow> = [
    {
      title: t('common.statusLabel'),
      key: 'status',
      width: 120,
      render: (_, job) => {
        const status = mapJobToClientRequestStatus(job);
        const overdue = isClientRequestOverdueUnscheduled(job);
        return (
          <Space size={4} wrap>
            <Tag color={getClientRequestStatusColor(status)}>
              {translateClientRequestStatus(t, status)}
            </Tag>
            {overdue ? <Tag color="error">{t('requests.overdueBadge')}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: t('common.date'),
      dataIndex: 'schedule_date',
      key: 'date',
      width: 120,
      render: (d: string) => dayjs(d).format('DD MMM YYYY'),
      sorter: (a, b) => a.schedule_date.localeCompare(b.schedule_date),
    },
    {
      title: t('common.route'),
      key: 'route',
      ellipsis: true,
      render: (_, job) => (
        <Text strong style={{ fontWeight: 500 }}>
          {requestRouteLabel(job, t)}
        </Text>
      ),
    },
    {
      title: t('common.pickup'),
      key: 'time',
      width: 90,
      render: (_, job) => {
        const time = job.job_legs?.[0]?.earliest_start;
        return time ? time.slice(0, 5) : t('common.noData');
      },
    },
    {
      title: t('common.passengers'),
      dataIndex: 'passenger_count',
      key: 'pax',
      width: 100,
      align: 'center',
    },
    {
      title: t('common.notes'),
      key: 'notes',
      width: 220,
      ellipsis: true,
      render: (_, job) => {
        const status = mapJobToClientRequestStatus(job);
        if (status === 'rejected' && job.rejection_reason) {
          return <Text type="danger">{job.rejection_reason}</Text>;
        }
        if (status === 'submitted') {
          return (
            <Text type={isClientRequestOverdueUnscheduled(job) ? 'danger' : 'secondary'}>
              {isClientRequestOverdueUnscheduled(job)
                ? t('requests.overdueNote')
                : t('requests.waitingForHq')}
            </Text>
          );
        }
        if (status === 'accepted') {
          return (
            <Text type={isClientRequestOverdueUnscheduled(job) ? 'danger' : 'secondary'}>
              {isClientRequestOverdueUnscheduled(job)
                ? t('requests.overdueNote')
                : t('requests.inScheduling')}
            </Text>
          );
        }
        if (status === 'scheduled') {
          return <Text type="secondary">{t('requests.published')}</Text>;
        }
        return t('common.noData');
      },
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_, job) => {
        const status = mapJobToClientRequestStatus(job);
        return (
          <>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openDetail(job);
              }}
            >
              {t('common.view')}
            </Button>
            {status === 'scheduled' && (
              <Button type="link" size="small" onClick={(e) => void openTrip(job, e)}>
                {t('common.trip')}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const subtitle =
    statusFilter !== 'all'
      ? t('requests.subtitleFiltered', {
          count: filtered.length,
          status: translateClientRequestStatus(t, statusFilter),
        })
      : t('requests.subtitle', { count: filtered.length });

  return (
    <div>
      <div className="client-page-header">
        <div>
          <Title level={3} className="client-page-title">{t('requests.title')}</Title>
          <Text className="client-page-subtitle">{subtitle}</Text>
        </div>
        <div className="client-actions">
          <Select
            value={statusFilter}
            style={{ minWidth: 160 }}
            onChange={(v) => setStatusFilter(v)}
            options={statusFilterOptions}
          />
          <Button icon={<ReloadOutlined />} onClick={() => void fetchRows()}>
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {orgId === null && !loading && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('requests.orgNotLinked')}
          description={t('requests.orgNotLinkedDesc')}
        />
      )}

      {loading || orgId === undefined ? (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          description={
            rows.length === 0
              ? hasTrips
                ? t('requests.noRequestsButHasTrips')
                : t('requests.noRequestsYet')
              : t('requests.noMatchFilter')
          }
        >
          {rows.length === 0 && (
            <Space wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/requests/new')}
              >
                {t('nav.newRequest')}
              </Button>
              {hasTrips && (
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => navigate('/trips')}
                >
                  {t('requests.viewTrips')}
                </Button>
              )}
            </Space>
          )}
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          pagination={{
            pageSize: 12,
            showSizeChanger: false,
            showTotal: (total) => t('requests.showTotal', { total }),
          }}
          scroll={{ x: 800 }}
          onRow={(job) => ({
            style: { cursor: 'pointer' },
            onClick: () => openDetail(job),
          })}
        />
      )}
    </div>
  );
}

export default Requests;
